/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

/**
 * Returns true if current playhead position is inside and advert break. False otherwise.
 * @param playheadMs the current playhead position in milliseconds
 * @param adBreak the advert break to check whether the playhead position is within the
 * advert break start and advert break end time or not
 */
export function isInAdvertBreak(playheadMs: number, adBreak: any): boolean {
  return adBreak.getStart() <= playheadMs && playheadMs < adBreak.getStart() + adBreak.getDuration();
}

/**
 * Returns the first active advert break whose start position is 0L.
 * @param timeline an array of advert breaks
 */
export function findPrerollAdvertBreakByTimeline(timeline: any[]): any {
  return timeline.find((advertBreak: any) => advertBreak.getStart() === 0 && advertBreak.isActive());
}

/**
 * Returns a preroll advert break if provided playhead position is inside an active
 * advert break.
 * @param playheadMs the current playhead position in milliseconds
 * @param timeline an array of advert breaks
 */
export function findPrerollAdvertBreakByPlayheadMsAndTimeline(playheadMs: number, timeline: any[]): any {
  const prerollAdvertBreak = findPrerollAdvertBreakByTimeline(timeline);

  if (prerollAdvertBreak && isInAdvertBreak(playheadMs, prerollAdvertBreak) && prerollAdvertBreak.isActive()) {
    return prerollAdvertBreak;
  }

  return null;
}

/**
 * Returns the total duration of all advert breaks whose playback time is prior to the
 * provided playhead position, in milliseconds
 * @param positionMs the current playhead position in milliseconds
 * @param timeline an array of advert breaks
 */
export function advertBreakDurationsPriorTo(positionMs: number, timeline: any = []): number {
  let offsetMs = timeline
    .filter((adBreak: any) => adBreak.getStart() + adBreak.getDuration() < positionMs)
    .map((adBreak: any) => adBreak.getDuration())
    .reduce((accumulator: number, adBreakDuration: number) => accumulator + adBreakDuration, 0);

  offsetMs += timeline
    .filter((adBreak: any) => isInAdvertBreak(positionMs, adBreak))
    .map((adBreak: any) => positionMs - adBreak.getStart())
    .reduce((accumulator: number, adBreakDuration: number) => accumulator + adBreakDuration, 0);

  return offsetMs;
}

/**
 * Search for the first postroll advert break. Once the advert break is found, this function
 * returns the advert break if the current playhead position is within the advert break's
 * time range.
 * @param playheadMs the current playhead position in milliseconds
 * @param timeline an array of advert breaks
 * @param sessionDurationMs the session duration in milliseconds
 */
export function findPostrollAdvertBreak(playheadMs: number, timeline: any[], sessionDurationMs: number): any {
  const postRollAdvertBreak = timeline.find(
    (advertBreak: any) => advertBreak.getStart() + advertBreak.getDuration() === sessionDurationMs,
  );

  if (postRollAdvertBreak && isInAdvertBreak(playheadMs, postRollAdvertBreak) && postRollAdvertBreak.isActive()) {
    return postRollAdvertBreak;
  }

  return null;
}

/**
 * Returns the active advert break in which the provided playhead position is within
 * the advert break's time range.
 * @param playheadMs the current playhead position in milliseconds
 * @param timeline an array of advert breaks
 */
export function findActiveAdvertBreak(playheadMs: number, timeline: any[]): any {
  return timeline.find((advertBreak: any) => isInAdvertBreak(playheadMs, advertBreak) && advertBreak.isActive());
}

/**
 * Returns the closest active advert break to the provided playhead position.
 * @param positionMs the current playhead position in milliseconds
 * @param timeline an array of advert breaks
 */
export function findClosestActiveAdvertBreakPriorTo(positionMs: number, timeline: any[]): any {
  let closest = null;

  timeline.forEach((advertBreak: any) => {
    if (advertBreak.getStart() + advertBreak.getDuration() < positionMs && advertBreak.isActive()) {
      closest = advertBreak;
    }
  });

  return closest;
}

/**
 * Returns the first midroll advert break within the provided timeline.
 * @param timeline an array of advert breaks
 */
export function findFirstMidrollAdvertBreak(timeline: any[]): any {
  return timeline.find((advertBreak: any) => advertBreak.getStart() > 0);
}

/**
 * Inactivates all advert breaks between the given playback position.
 * @param startMs start playback position in milliseconds
 * @param endMs end playback position in milliseconds
 * @param timeline an array of advert breaks
 */
export function setInactiveAllAdvertBreaksBetween(startMs: number, endMs: number, timeline: any[]): void {
  timeline.forEach((adBreak: any) => {
    if (adBreak.isActive() && adBreak.getStart() >= startMs && adBreak.getStart() + adBreak.getDuration() <= endMs) {
      adBreak.setInactive();
    }
  });
}

/**
 * Returns the total duration of all advert breaks in the provided session, in milliseconds.
 * @param session
 */
export function getVODSessionAdsDuration(session: any, breakType: any): number {
  if (!session) {
    return 0;
  }

  const adBreaks = session.getAdBreaksByType(breakType).filter((adBreak: any) => adBreak.getAdverts().length > 0);

  return allAdvertBreaksDuration(adBreaks);
}

/**
 * Returns the total duration of all advert breaks in the provided timeline, in milliseconds.
 * @param timeline an array of advert breaks
 */
export function allAdvertBreaksDuration(timeline: any[]): number {
  return timeline
    .map((adBreak: any) => adBreak.getDuration())
    .reduce((accumulator: number, advertBreakDuration: number) => accumulator + advertBreakDuration, 0);
}

/**
 * Returns the total duration of all advert breaks in the provided session prior to positionMs, in milliseconds.
 * @param positionMs
 * @param session
 */
export function getVODSessionDurationPriorTo(positionMs: number, session: any, breakType: any): number {
  if (!session) {
    return 0;
  }

  const adBreaks = session.getAdBreaksByType(breakType).filter((adBreak: any) => adBreak.getAdverts().length > 0);

  return advertBreakDurationsPriorTo(positionMs, adBreaks);
}

export function getSeekPositionFor(playheadMs: number, session: any, breakType: any): number {
  if (!session) {
    return playheadMs;
  }

  playheadMs += session
    .getAdBreaksByType(breakType)
    //.filter((adBreak: any) => adBreak.getPosition() === 'preroll' || adBreak.getStart() < playheadMs)
    .filter((adBreak: any) => adBreak.getStart() === 0 || adBreak.getStart() < playheadMs)
    .map((adBreak: any) => adBreak.getDuration())
    .reduce((accumulator: number, adBreakDuration: number) => accumulator + adBreakDuration, 0);

  return playheadMs;
}
